import { ReactNode } from 'react';
import aiGenerationDisabledImg from '@/assets/images/ai-generation-disabled.webp';
import aiGenerationEnabledImg from '@/assets/images/ai-generation-enabled.webp';
import { ShieldTick } from '@/components/icons';
import { getAISecurityInfoModalOptions } from '@/components/modals/AISecurityInfoModal';
import { Button } from '@/components/ui/button';
import { useModal } from '@/hooks/use-modal';

interface AICardShellProps {
    /** Whether the CTA below can actually run — picks the illustration's mood. */
    ready?: boolean;
    children?: ReactNode;
}

/**
 * The AI region of a section panel — a region, not a card.
 *
 * It used to be a bordered box inside a bordered panel inside a bordered
 * sidebar: six nested containers and twenty-four rounded corners in a 340 px
 * column. Nothing here keeps a shape now except the button — an illustration
 * opens, and space does the rest.
 */
export default function AICardShell({ ready = true, children }: AICardShellProps) {
    const { openWizardModal } = useModal();

    const handleOpenSecurityInfo = () => {
        openWizardModal(getAISecurityInfoModalOptions());
    };

    return (
        <div className="flex flex-col gap-4">
            {/* It says "a IA escreve esta parte" before a single row is read,
                and swaps mood with the button. Two separate assets rather than
                one filtered asset, so the blocked state changes the drawing and
                not just its saturation.

                Sized off the column rather than a fixed width: the sidebar is
                user-resizable from ~320 px to ~600 px, and a fixed box that
                looks right at one end reads as a stamp at the other. `max-w`
                keeps it from becoming a masthead on a wide panel. */}
            <div className="flex justify-center">
                <img
                    src={ready ? aiGenerationEnabledImg : aiGenerationDisabledImg}
                    alt=""
                    // Intrinsic size, so the panel reserves the box instead of
                    // reflowing when the art lands.
                    width={ready ? 953 : 898}
                    height={ready ? 595 : 590}
                    decoding="async"
                    className="h-auto w-1/2 min-w-[124px] max-w-[200px]"
                />
            </div>

            {/* No copy between the illustration and the list. The sentence that
                used to sit here explained what the button already says, and the
                list below it names its own contents. */}
            {children}

            <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleOpenSecurityInfo}
                className="w-full gap-1.5 font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
                <ShieldTick className="size-3.5" />
                Saiba como protegemos seus dados
            </Button>
        </div>
    );
}
